import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getApiUrl } from "../utils/api";
import { Link } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import BadgeIcon from "./BadgeIcon";
import { UserContext } from "../context/UserContext";
import {
  getAvatarUrl,
  DEFAULT_AVATAR_DATA_URL,
  FALLBACK_AVATAR_SVG,
} from "../utils/avatar";

function avatarOnError(e) {
  e.currentTarget.src =
    e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
      ? FALLBACK_AVATAR_SVG
      : DEFAULT_AVATAR_DATA_URL;
}

function InviteUserRow({ person, invitingId, onInvite }) {
  return (
    <li className="app-right-sidebar-item">
      <Link
        to={`/users/${person.username}`}
        className="app-right-sidebar-user"
        title={person.username}
      >
        <img
          src={getAvatarUrl(person.avatar)}
          alt=""
          className="app-right-sidebar-avatar"
          onError={avatarOnError}
        />
        <span className="app-right-sidebar-name">
          {person.username}
          <BadgeIcon type={person.badge} className="text-[#f26522]" />
        </span>
      </Link>
      {person.inviteStatus === "available" ? (
        <button
          type="button"
          onClick={() => onInvite(person._id)}
          disabled={invitingId === person._id}
          className="app-right-sidebar-invite"
        >
          {invitingId === person._id ? "..." : "Invite"}
        </button>
      ) : person.inviteStatus === "pending" ? (
        <span className="app-right-sidebar-status app-right-sidebar-status--pending">
          Sent
        </span>
      ) : (
        <span className="app-right-sidebar-status">Member</span>
      )}
    </li>
  );
}

function QuillIllustration() {
  return (
    <svg
      className="app-right-sidebar-quill"
      viewBox="0 0 120 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M18 62c8-14 22-28 38-36 6-3 12-5 18-6-2 8-6 16-12 22-8 8-18 14-28 18-5 2-11 3-16 2z"
        stroke="#f26522"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M56 20c10-2 20 0 28 6 4 3 7 7 9 12"
        stroke="#f5a623"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <ellipse cx="88" cy="58" rx="14" ry="6" stroke="#f26522" strokeWidth="1.5" />
      <path d="M82 58h12" stroke="#f26522" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export default function GroupInviteSection({
  groupId,
  groupName,
  instanceId = "default",
}) {
  const { user } = useContext(UserContext);
  const sectionRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const searchInputId = `group-invite-search-${instanceId}-${groupId}`;

  const [followers, setFollowers] = useState([]);
  const [followersPagination, setFollowersPagination] = useState({
    currentPage: 1,
    hasNextPage: false,
  });
  const [loadingMoreFollowers, setLoadingMoreFollowers] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invitingId, setInvitingId] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);

  const loadFollowers = useCallback(async (page = 1, append = false) => {
    if (!user || !groupId) {
      setFollowers([]);
      return;
    }

    if (append) setLoadingMoreFollowers(true);
    else setLoading(true);
    setInviteError("");
    try {
      const res = await fetch(
        getApiUrl(`/api/groups/${groupId}/followers?page=${page}&limit=20`),
        { credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data.followers)
            ? data.followers
            : [];
        const pagination = Array.isArray(data)
          ? { currentPage: 1, hasNextPage: false }
          : {
              currentPage: data.pagination?.currentPage ?? page,
              hasNextPage: Boolean(data.pagination?.hasNextPage),
            };
        setFollowers((prev) => (append ? [...prev, ...list] : list));
        setFollowersPagination(pagination);
      } else if (!append) {
        setFollowers([]);
      }
    } catch (err) {
      console.error("Failed to load followers for invite:", err);
      if (!append) setFollowers([]);
    } finally {
      if (append) setLoadingMoreFollowers(false);
      else setLoading(false);
    }
  }, [user, groupId]);

  const searchUsers = useCallback(
    async (query) => {
      if (!groupId || !user) return;
      const q = query.trim();
      if (q.length < 2) {
        setSearchResults([]);
        setSearching(false);
        return;
      }

      setSearching(true);
      try {
        const res = await fetch(
          getApiUrl(`/api/groups/${groupId}/search-users?q=${encodeURIComponent(q)}`),
          { credentials: "include" }
        );
        if (res.ok) {
          const data = await res.json();
          setSearchResults(Array.isArray(data) ? data : []);
        } else {
          setSearchResults([]);
        }
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    },
    [groupId, user]
  );

  useEffect(() => {
    loadFollowers(1, false);
    setInviteOpen(false);
    setSearchQuery("");
    setSearchResults([]);
  }, [loadFollowers]);

  useEffect(() => {
    const onInvitesChanged = (e) => {
      if (!groupId || String(e.detail?.groupId) !== String(groupId)) return;
      if (searchQuery.trim().length >= 2) searchUsers(searchQuery);
    };
    window.addEventListener("group-invites-changed", onInvitesChanged);
    return () => window.removeEventListener("group-invites-changed", onInvitesChanged);
  }, [groupId, searchQuery, searchUsers]);

  useEffect(() => {
    const onOpenInvite = (e) => {
      if (!groupId || String(e.detail?.groupId) !== String(groupId)) return;
      const source = e.detail?.source;
      if (source && source !== instanceId) return;
      setInviteOpen(true);
      if (instanceId === "panel") {
        requestAnimationFrame(() => {
          sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
      }
    };
    window.addEventListener("group-open-invite", onOpenInvite);
    return () => window.removeEventListener("group-open-invite", onOpenInvite);
  }, [groupId, instanceId]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const markInvited = (userId) => {
    const mark = (list) =>
      list.map((item) =>
        item._id === userId ? { ...item, inviteStatus: "pending" } : item
      );
    setFollowers((prev) => mark(prev));
    setSearchResults((prev) => mark(prev));
  };

  const handleInvite = async (targetUserId) => {
    if (!groupId) return;
    setInvitingId(targetUserId);
    setInviteError("");
    try {
      const res = await fetch(
        getApiUrl(`/api/groups/${groupId}/invite`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ userId: targetUserId }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send invite");

      markInvited(targetUserId);
      window.dispatchEvent(
        new CustomEvent("group-invites-changed", { detail: { groupId } })
      );
    } catch (err) {
      setInviteError(err.message || "Failed to send invite.");
    } finally {
      setInvitingId(null);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      searchUsers(value);
    }, 250);
  };

  if (!user || !groupId) return null;

  const showSearch = searchQuery.trim().length >= 2;

  return (
    <div ref={sectionRef}>
      {!inviteOpen ? (
        <section className="app-right-sidebar-card app-right-sidebar-card--invite group-rail__invite">
          <QuillIllustration />
          <p className="app-right-sidebar-invite-cta">
            Love this group? Invite your friends and grow our poetry circle.
          </p>
          <button
            type="button"
            className="app-right-sidebar-primary-btn"
            onClick={() => setInviteOpen(true)}
          >
            Invite members
          </button>
        </section>
      ) : (
        <section className="app-right-sidebar-card app-right-sidebar-card--invite">
          <div className="app-right-sidebar-invite-header">
            <h2 className="app-right-sidebar-card__title app-right-sidebar-card__title--dark">
              Invite members
            </h2>
            <button
              type="button"
              className="app-right-sidebar-close"
              onClick={() => setInviteOpen(false)}
            >
              Close
            </button>
          </div>
          <p className="app-right-sidebar-hint">
            Search for any user or invite your followers to join{" "}
            <span className="font-semibold text-[#333333]">{groupName}</span>.
          </p>

          <label className="app-right-sidebar-search-label" htmlFor={searchInputId}>
            Search users
          </label>
          <div className="app-right-sidebar-search-wrap">
            <FaSearch className="app-right-sidebar-search-icon" aria-hidden="true" />
            <input
              id={searchInputId}
              type="search"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Pen name or name"
              className="app-right-sidebar-search"
              autoComplete="off"
            />
          </div>

          {inviteError && (
            <p className="app-right-sidebar-error" role="alert">
              {inviteError}
            </p>
          )}

          {showSearch && (
            <div className="app-right-sidebar-section">
              <p className="app-right-sidebar-section-title">Search results</p>
              {searching ? (
                <p className="text-sm text-[#777777] px-1">Searching...</p>
              ) : searchResults.length === 0 ? (
                <p className="text-sm text-[#777777] px-1 leading-relaxed">
                  No users found.
                </p>
              ) : (
                <ul className="app-right-sidebar-list app-right-sidebar-list--compact">
                  {searchResults.map((person) => (
                    <InviteUserRow
                      key={person._id}
                      person={person}
                      invitingId={invitingId}
                      onInvite={handleInvite}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="app-right-sidebar-section">
            <p className="app-right-sidebar-section-title">Your followers</p>
            {loading ? (
              <p className="text-sm text-[#777777] px-1">Loading followers...</p>
            ) : followers.length === 0 ? (
              <p className="text-sm text-[#777777] px-1 leading-relaxed">
                You don&apos;t have any followers yet.
              </p>
            ) : (
              <>
              <ul className="app-right-sidebar-list">
                {followers.map((follower) => (
                  <InviteUserRow
                    key={follower._id}
                    person={follower}
                    invitingId={invitingId}
                    onInvite={handleInvite}
                  />
                ))}
              </ul>
              {followersPagination.hasNextPage && (
                <button
                  type="button"
                  className="text-xs font-semibold text-[#f26522] hover:underline mt-2 px-1 disabled:opacity-60"
                  disabled={loadingMoreFollowers}
                  onClick={() =>
                    loadFollowers((followersPagination.currentPage || 1) + 1, true)
                  }
                >
                  {loadingMoreFollowers ? "Loading..." : "Load more followers"}
                </button>
              )}
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
